import type {
  PreviewAutomationHost,
  PreviewAutomationRequest,
  PreviewAutomationResponse,
  PreviewAutomationStreamEvent,
} from "@t3tools/contracts";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import {
  PreviewAutomationOperationError,
  type PreviewAutomationOperationContext,
  serializePreviewAutomationHostError,
} from "./previewAutomationErrors";

type AutomationStreamResult<E> = AsyncResult.AsyncResult<PreviewAutomationStreamEvent, E>;

type ThreadRequestSchedule = {
  readonly pending: Set<Promise<void>>;
  barrier: Promise<void> | undefined;
};

const MAX_PENDING_AUTOMATION_REQUESTS = 256;

export function serializePreviewAutomationError(
  error: unknown,
  context: PreviewAutomationOperationContext,
): NonNullable<PreviewAutomationResponse["error"]> {
  return serializePreviewAutomationHostError(
    PreviewAutomationOperationError.fromCause({ ...context, cause: error }),
  );
}

export function createPreviewAutomationRequestConsumerAtom<E>(options: {
  readonly requestsAtom: Atom.Atom<AutomationStreamResult<E>>;
  readonly clientId: PreviewAutomationHost["clientId"];
  readonly connectionAtom: Atom.Writable<PreviewAutomationStreamEvent["connectionId"] | null>;
  readonly environmentId: PreviewAutomationHost["environmentId"];
  readonly requestHandlerAtom: Atom.Atom<{
    readonly handle: (request: PreviewAutomationRequest) => Promise<unknown>;
  }>;
  readonly respond: (response: PreviewAutomationResponse) => Promise<unknown>;
  readonly label: string;
  /** Injectable for deterministic deadline tests; production uses wall clock time. */
  readonly now?: () => number;
}): Atom.Atom<void> {
  return Atom.make((get) => {
    get.mount(options.connectionAtom);
    get.mount(options.requestHandlerAtom);
    let disposed = false;
    let activeConnectionId: PreviewAutomationStreamEvent["connectionId"] | null = null;
    let connectionExplicitlyAnnounced = false;
    let reportedConnectionId: PreviewAutomationStreamEvent["connectionId"] | null = null;
    let requestsVersion = 0;
    let generation = 0;
    let pendingRequestCount = 0;
    const requestLanes = new Map<string, Promise<void>>();
    const threadSchedules = new Map<string, ThreadRequestSchedule>();

    const requestLaneKey = (request: PreviewAutomationRequest): string =>
      JSON.stringify([options.environmentId, request.threadId, request.tabId ?? null]);
    const threadScheduleKey = (request: PreviewAutomationRequest): string =>
      JSON.stringify([options.environmentId, request.threadId]);
    const isControlTransition = (request: PreviewAutomationRequest): boolean =>
      request.operation === "controlTake" || request.operation === "controlReturn";

    const processRequest = async (
      event: Extract<PreviewAutomationStreamEvent, { readonly type: "request" }>,
      requestGeneration: number,
    ): Promise<void> => {
      const request = event.request;
      if (
        disposed ||
        requestGeneration !== generation ||
        activeConnectionId !== event.connectionId ||
        (request.deadlineAtMillis !== undefined &&
          (options.now?.() ?? Date.now()) >= request.deadlineAtMillis)
      ) {
        return;
      }
      let response: PreviewAutomationResponse;
      try {
        const value = await get.once(options.requestHandlerAtom).handle(request);
        response = {
          clientId: options.clientId,
          connectionId: event.connectionId,
          requestId: request.requestId,
          ok: true,
          ...(value === undefined ? {} : { result: value }),
        };
      } catch (error) {
        response = {
          clientId: options.clientId,
          connectionId: event.connectionId,
          requestId: request.requestId,
          ok: false,
          error: serializePreviewAutomationError(error, {
            requestId: request.requestId,
            operation: request.operation,
            environmentId: options.environmentId,
            threadId: request.threadId,
            tabId: request.tabId ?? null,
          }),
        };
      }
      if (
        disposed ||
        requestGeneration !== generation ||
        activeConnectionId !== event.connectionId
      ) {
        return;
      }
      try {
        await options.respond(response);
      } catch {
        // A disconnected broker cannot be repaired by the host response path.
      }
    };

    const enqueueRequest = (
      event: Extract<PreviewAutomationStreamEvent, { readonly type: "request" }>,
    ): void => {
      if (disposed || pendingRequestCount >= MAX_PENDING_AUTOMATION_REQUESTS) return;
      const requestGeneration = generation;
      pendingRequestCount += 1;
      const key = requestLaneKey(event.request);
      const threadKey = threadScheduleKey(event.request);
      const schedule =
        threadSchedules.get(threadKey) ??
        (() => {
          const created: ThreadRequestSchedule = {
            pending: new Set<Promise<void>>(),
            barrier: undefined,
          };
          threadSchedules.set(threadKey, created);
          return created;
        })();
      const previous = requestLanes.get(key) ?? Promise.resolve();
      const dependencies = isControlTransition(event.request)
        ? [...schedule.pending]
        : [previous, ...(schedule.barrier ? [schedule.barrier] : [])];
      const current = Promise.all(dependencies).then(
        () => processRequest(event, requestGeneration),
        () => processRequest(event, requestGeneration),
      );
      requestLanes.set(key, current);
      schedule.pending.add(current);
      if (isControlTransition(event.request)) schedule.barrier = current;
      const cleanup = () => {
        if (requestLanes.get(key) === current) requestLanes.delete(key);
        schedule.pending.delete(current);
        pendingRequestCount = Math.max(0, pendingRequestCount - 1);
        if (schedule.barrier === current) schedule.barrier = undefined;
        if (schedule.pending.size === 0 && schedule.barrier === undefined) {
          threadSchedules.delete(threadKey);
        }
      };
      void current.then(cleanup, cleanup);
    };

    const consume = (result: AutomationStreamResult<E>) => {
      if (!AsyncResult.isSuccess(result)) return;
      const event = result.value;
      if (event.type === "connected") {
        if (activeConnectionId !== null && activeConnectionId !== event.connectionId) {
          generation += 1;
        }
        activeConnectionId = event.connectionId;
        connectionExplicitlyAnnounced = true;
      } else if (activeConnectionId === null) {
        activeConnectionId = event.connectionId;
      } else if (activeConnectionId !== event.connectionId) {
        if (connectionExplicitlyAnnounced) return;
        generation += 1;
        activeConnectionId = event.connectionId;
      }
      if (reportedConnectionId !== event.connectionId) {
        reportedConnectionId = event.connectionId;
        get.set(options.connectionAtom, event.connectionId);
      }
      if (event.type === "connected") {
        return;
      }
      enqueueRequest(event);
    };

    get.addFinalizer(() => {
      disposed = true;
      generation += 1;
      pendingRequestCount = 0;
      requestLanes.clear();
      threadSchedules.clear();
    });
    const initialRequest = get.once(options.requestsAtom);
    if (AsyncResult.isSuccess(initialRequest)) {
      activeConnectionId = initialRequest.value.connectionId;
      connectionExplicitlyAnnounced = initialRequest.value.type === "connected";
      if (initialRequest.value.type === "connected") {
        reportedConnectionId = initialRequest.value.connectionId;
        get.set(options.connectionAtom, initialRequest.value.connectionId);
      }
    }
    get.subscribe(options.requestsAtom, (result) => {
      requestsVersion += 1;
      consume(result);
    });
    queueMicrotask(() => {
      const initialConnectionWasSkipped =
        AsyncResult.isSuccess(initialRequest) &&
        initialRequest.value.connectionId === activeConnectionId &&
        initialRequest.value.connectionId !== reportedConnectionId;
      if (!disposed && (requestsVersion === 0 || initialConnectionWasSkipped)) {
        consume(initialRequest);
      }
    });
  }).pipe(Atom.setIdleTTL(0), Atom.withLabel(options.label));
}
